import { deriveSizeTier, type SizeTier } from '@designdeck/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ImageOff } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  PageHeader,
  Pill,
  PillGroup,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import type {
  AdminMockupTemplate as ProductMockupTemplate,
  UpdatePrintAreaPayload,
} from '@/api/admin';
import {
  useAdminProduct,
  useCalibrateTemplate,
  useUpdateMockupTemplate,
  useUpdatePrintArea,
} from '@/hooks/useAdminProducts';
import {
  AreaCarryOverFields,
  CalibrationStatusPill,
  MM_FIELD_LABEL,
  NotSelectableBanner,
  MockupLayersPanel,
  MOCKUP_TYPES,
  PositionSurchargeField,
  PositionDecorationMethodsPanel,
  ProductCarryOverPanel,
  ProductMethodPriceOverrides,
  PrintAreaEditor,
  buildDraft,
  buildEditorOverlays,
  calibrationErrorMessage,
  calibrationSavedMessage,
  draftsEqual,
  editorColorLayer,
  editorCutoutMaskUrl,
  frameFromDrawn,
  frameSourceOf,
  frameStatusOf,
  framesEqual,
  missingMmField,
  printAreaFieldsEqual,
  type EditableArea,
  type EditorOverlay,
  type PrintAreaDraft,
} from '@/components/admin';
import {
  normalizeLayerOrder,
  proxyStorageUrl,
} from '@/lib/product-transformer';

// ═══════════════════════════════════════════════════════════════════
// TYPES & HELPERS
// ═══════════════════════════════════════════════════════════════════

const SIZE_TIER_LABELS: Record<SizeTier, string> = {
  KLEIN: 'Klein',
  STANDARD: 'Standard',
  RUNDUM: 'Rundum',
};

type SidebarTab = 'area' | 'mockups';

function isSidebarTab(value: string): value is SidebarTab {
  return value === 'area' || value === 'mockups';
}

const TOAST_OPTS = { position: 'bottom-right' as const };

/** Checkerboard behind transparent swatches and mockups, in the paper tokens. */
const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, var(--paper-3) 25%, transparent 25%, transparent 75%, var(--paper-3) 75%)',
  backgroundSize: '8px 8px',
};

/** The print-area payload of a draft: millimetres, rotation, carry-over. */
function printAreaPayload(draft: PrintAreaDraft): UpdatePrintAreaPayload {
  return {
    rotation: draft.rotation,
    maxWidthMm: draft.maxWidthMm,
    maxHeightMm: draft.maxHeightMm,
    // The API accepts only the RUNDUM marker since the carry-over
    // settings; a KLEIN value still shown here is cleared on save.
    sizeTierOverride: draft.sizeTierOverride === 'RUNDUM' ? 'RUNDUM' : null,
    // RUNDUM precedes every percent; the API refuses the pair.
    carryOverPercent:
      draft.sizeTierOverride === 'RUNDUM' ? null : draft.carryOverPercent,
    carryOverAnchor: draft.carryOverAnchor,
    carryOverTopOffsetMm: draft.carryOverTopOffsetMm,
  };
}

// ═══════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════

/**
 * Admin: configure one product.
 * Left: position tabs. Center: visual design-area editor on the mockup.
 * Right: numeric design-area fields and mockup layer management.
 * The admin shell around the page carries the section navigation.
 */
export function AdminProductConfigPage() {
  const { productId } = useParams<{ productId: string }>();
  const { data: product, isLoading, isError } = useAdminProduct(productId);
  const updatePrintArea = useUpdatePrintArea(productId ?? '');
  const calibrateTemplate = useCalibrateTemplate(productId ?? '');

  const positions = useMemo(() => product?.positions ?? [], [product]);

  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    null
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('area');
  const [draft, setDraft] = useState<PrintAreaDraft | null>(null);
  const [baseline, setBaseline] = useState<PrintAreaDraft | null>(null);
  const [layerVisibility, setLayerVisibility] = useState({
    heathered: true,
    texture: true,
    shadow: true,
    overlay: true,
    mask: true,
  });
  // Layer 0 simulation: preview the mockup on a product color like the creator
  const [simulatedColorId, setSimulatedColorId] = useState<string | null>(null);
  // Layer 2 preview: show a sample design inside the design area
  const [showSampleDesign, setShowSampleDesign] = useState(true);
  // The last save's answer said the product is not selectable (R13).
  const [notSelectable, setNotSelectable] = useState<{
    warning: string | null;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);

  const selectedPosition = useMemo(
    () =>
      positions.find((p) => p.id === selectedPositionId) ??
      positions.find((p) => p.isDefault) ??
      positions[0],
    [positions, selectedPositionId]
  );

  const positionTemplates = useMemo<ProductMockupTemplate[]>(
    () =>
      (product?.mockupTemplates ?? []).filter(
        (t) => t.positionKey === selectedPosition?.slug
      ),
    [product, selectedPosition]
  );

  const activeTemplate = useMemo(
    () =>
      positionTemplates.find((t) => t.id === selectedTemplateId) ??
      positionTemplates.find((t) => t.isDefault) ??
      positionTemplates[0],
    [positionTemplates, selectedTemplateId]
  );

  const mockupWidth = activeTemplate?.mockupWidthPx ?? 500;
  const mockupHeight = activeTemplate?.mockupHeightPx ?? 500;

  // Every template, default or not, carries its own calibration (R6): the
  // frame is drawn and saved per template, the millimetres, rotation and
  // carry-over belong to the position's print area.
  const frameSource = frameSourceOf(selectedPosition, activeTemplate);
  const frameStatus = frameStatusOf(selectedPosition, activeTemplate);

  // The admin preview shows one mockup large: prefer the high-res rendering
  // asset, like the editor canvas does. Cards elsewhere keep the creator asset.
  const baseImageUrl = proxyStorageUrl(
    activeTemplate?.renderingImageUrl ||
      activeTemplate?.imageUrl ||
      product?.mockupImageUrl
  );
  const hasMockup = !!baseImageUrl;

  // Unsaved edits = draft differs from the baseline captured at load time.
  // (A start suggestion on an uncalibrated template does not count.)
  const hasUnsavedEdits =
    !!draft && !!baseline && !draftsEqual(draft, baseline);
  const frameDirty = !!draft && !!baseline && !framesEqual(draft, baseline);
  const areaDirty =
    !!draft && !!baseline && !printAreaFieldsEqual(draft, baseline);
  const mmChanged =
    !!draft &&
    !!baseline &&
    (draft.maxWidthMm !== baseline.maxWidthMm ||
      draft.maxHeightMm !== baseline.maxHeightMm);
  // The frame is saved when it was moved, when the template has no
  // calibration yet (suggestion or draft to review), or when new mm put it
  // to review anyway.
  const frameToSave =
    !!activeTemplate &&
    (frameDirty || frameSource !== 'calibration' || mmChanged);
  // Calibrating needs both millimetre values (R9).
  const missingField = draft ? missingMmField(draft) : null;
  const canCalibrate = frameToSave && !missingField;
  const canSave = hasMockup && !!draft && (canCalibrate || areaDirty);
  const isSaving = updatePrintArea.isPending || calibrateTemplate.isPending;

  // Height follows the width and the millimetres (R8), on every edit.
  const lockFrame = useCallback(
    (next: PrintAreaDraft): PrintAreaDraft => ({
      ...next,
      ...frameFromDrawn(next, next, mockupWidth),
    }),
    [mockupWidth]
  );

  // Re-initialize draft + baseline whenever the position, the active
  // template or the saved area changes
  useEffect(() => {
    if (!selectedPosition) return;
    const next = buildDraft(
      selectedPosition,
      activeTemplate,
      mockupWidth,
      mockupHeight
    );
    setDraft(next);
    setBaseline(next);
    setSaveError(null);
  }, [selectedPosition, activeTemplate, mockupWidth, mockupHeight]);

  // Browser-level guard: warn before closing/reloading with unsaved edits
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedEdits]);

  // Router-level guard: covers in-app navigation incl. browser back/forward
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedEdits && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('You have unsaved design area changes. Discard them?')) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  useEffect(() => {
    document.title = product
      ? `${product.name} · Product Admin – DesignDeck`
      : 'Product Admin – DesignDeck';
    return () => {
      document.title = 'DesignDeck';
    };
  }, [product]);

  const confirmDiscard = useCallback((): boolean => {
    if (!hasUnsavedEdits) return true;
    return window.confirm(
      'You have unsaved design area changes. Discard them?'
    );
  }, [hasUnsavedEdits]);

  const handleSelectPosition = (positionId: string) => {
    if (positionId === selectedPosition?.id) return;
    if (!confirmDiscard()) return;
    setSelectedPositionId(positionId);
    setSelectedTemplateId(null);
  };

  const handleSelectTemplate = (templateId: string) => {
    if (templateId === activeTemplate?.id) return;
    if (!confirmDiscard()) return;
    setSelectedTemplateId(templateId);
  };

  // Type editing lives on the sidebar badge: clicking it swaps the badge
  // for a native select that saves immediately.
  const updateTemplate = useUpdateMockupTemplate(productId ?? '');
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);

  const handleChangeType = async (templateId: string, mockupType: string) => {
    setEditingTypeId(null);
    try {
      await updateTemplate.mutateAsync({ templateId, payload: { mockupType } });
      toast.success('Mockup type updated', TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const simulatedColor = useMemo(
    () => (product?.colors ?? []).find((c) => c.id === simulatedColorId),
    [product, simulatedColorId]
  );

  const overlays = useMemo<EditorOverlay[]>(
    () => buildEditorOverlays(activeTemplate, simulatedColor, layerVisibility),
    [activeTemplate, layerVisibility, simulatedColor]
  );

  const cutoutMaskUrl = editorCutoutMaskUrl(activeTemplate);

  // Other calibrated templates of the position new mm put to review (R28).
  const templatesToReview = positionTemplates.filter(
    (t) =>
      t.id !== activeTemplate?.id &&
      frameStatusOf(selectedPosition, t) === 'calibrated'
  ).length;

  /**
   * Saves the print area (mm, rotation, carry-over) when it changed, then
   * the frame as the active template's calibration. The frame never sends
   * a height: it follows from the millimetres.
   */
  const handleSave = async (options: { confirmDraft?: boolean } = {}) => {
    if (!selectedPosition || !draft || !baseline) return;
    if (
      mmChanged &&
      templatesToReview > 0 &&
      !window.confirm(
        `Geänderte mm setzen ${templatesToReview} weitere kalibrierte Vorlage(n) dieser Position auf „prüfen“. Speichern?`
      )
    ) {
      return;
    }
    const saved = draft;
    // "Entwurf übernehmen" confirms the stored draft as it is.
    const frame = options.confirmDraft ? baseline : saved;
    setSaveError(null);
    setSavedHint(null);
    let selectability: {
      productSelectable: boolean;
      warning: string | null;
    } | null = null;
    try {
      if (areaDirty) {
        selectability = await updatePrintArea.mutateAsync({
          positionId: selectedPosition.id,
          payload: printAreaPayload(saved),
        });
      }
      if (activeTemplate && (canCalibrate || options.confirmDraft)) {
        const result = await calibrateTemplate.mutateAsync({
          templateId: activeTemplate.id,
          payload: {
            x: frame.x,
            y: frame.y,
            width: frame.width,
            ...(options.confirmDraft && { confirmDraft: true }),
          },
        });
        selectability = result;
        const hint = calibrationSavedMessage(result);
        setSavedHint(hint);
        toast.success(hint, TOAST_OPTS);
      } else {
        toast.success(
          `Design area saved for "${selectedPosition.name}"`,
          TOAST_OPTS
        );
      }
      setBaseline(saved);
      setNotSelectable(
        selectability && !selectability.productSelectable
          ? { warning: selectability.warning }
          : null
      );
    } catch (error) {
      const message = calibrationErrorMessage(error);
      setSaveError(message);
      toast.error(message, TOAST_OPTS);
    }
  };

  const updateDraftField = (field: keyof PrintAreaDraft, value: number) => {
    setDraft((prev) => (prev ? lockFrame({ ...prev, [field]: value }) : prev));
  };

  if (isLoading) {
    return <LoadingState label="Loading product…" />;
  }

  if (isError || !product) {
    return (
      <Card className="mx-auto w-full max-w-7xl">
        <p className="text-sm text-danger">Failed to load product.</p>
        <Link
          to="/admin/products"
          className="mt-3 inline-block text-sm font-medium text-ink-2 transition-colors hover:text-ink"
        >
          ← Back to products
        </Link>
      </Card>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader
        eyebrow="Admin · Product"
        title={product.name}
        description={product.artNumber || product.slug}
        action={
          <>
            {hasUnsavedEdits && (
              <StatusPill variant="warning">Unsaved changes</StatusPill>
            )}
            <Link
              to="/admin/products"
              className="text-sm font-medium text-ink-2 transition-colors hover:text-ink"
            >
              ← Products
            </Link>
          </>
        }
      />

      <div className="flex gap-6">
        {/* Left: positions with their templates as sub-entries */}
        <aside className="w-64 flex-shrink-0">
          <Card padding="sm">
            <CardHeader className="mb-3">
              <CardTitle className="text-sm">Positions</CardTitle>
            </CardHeader>
            <PillGroup
              mode="toggle"
              aria-label="Positions"
              className="w-full flex-col items-stretch gap-1"
            >
              {positions.map((position) => (
                <div key={position.id} className="w-full min-w-0">
                  <Pill
                    active={selectedPosition?.id === position.id}
                    onClick={() => handleSelectPosition(position.id)}
                    className="h-auto w-full flex-col items-start gap-0 px-3 py-2 text-left"
                  >
                    <span className="block w-full truncate">
                      {position.name}
                    </span>
                    <span
                      className={`text-xs font-normal ${
                        selectedPosition?.id === position.id ? '' : 'text-mute'
                      }`}
                    >
                      {position.printArea ? 'configured' : 'no design area'}
                      {position.printSurcharge == null && ' · unpriced'}
                    </span>
                  </Pill>

                  {/* Templates of the active position (accordion). The type
                      badge and its inline select sit next to the pill, not
                      inside it: no interactive element nests in another. */}
                  {selectedPosition?.id === position.id && (
                    <div className="ml-2 mt-1 space-y-1 border-l border-line pl-2">
                      {positionTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="flex items-center gap-1"
                        >
                          <Pill
                            active={activeTemplate?.id === template.id}
                            onClick={() => handleSelectTemplate(template.id)}
                            className="h-8 min-w-0 flex-1 justify-start gap-1.5 px-2 text-xs"
                          >
                            <span
                              className="min-w-0 flex-1 truncate"
                              title={template.name}
                            >
                              {template.name}
                            </span>
                            {template.isDefault && (
                              <StatusPill
                                variant="neutral"
                                title="Default template — used by the creator"
                                className="h-4 px-1 text-[9px]"
                              >
                                default
                              </StatusPill>
                            )}
                            <CalibrationStatusPill
                              status={frameStatusOf(position, template)}
                              className="h-4 px-1 text-[9px]"
                            />
                          </Pill>
                          {editingTypeId === template.id ? (
                            <select
                              autoFocus
                              aria-label="Mockup type"
                              value={template.mockupType}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handleChangeType(template.id, e.target.value)
                              }
                              onBlur={() => setEditingTypeId(null)}
                              className="rounded-chip bg-paper px-0.5 py-px text-[10px] text-ink ring-1 ring-inset ring-line focus:outline-none"
                            >
                              {/* Synced templates may carry types outside the
                                  standard list (e.g. "hollowmen") — keep the
                                  current value selectable */}
                              {(MOCKUP_TYPES.includes(template.mockupType)
                                ? MOCKUP_TYPES
                                : [template.mockupType, ...MOCKUP_TYPES]
                              ).map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <button
                              type="button"
                              title="Click to change the mockup type"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTypeId(template.id);
                              }}
                              className="rounded-chip bg-paper-3 px-1.5 py-px font-mono text-[10px] font-medium text-ink-2 transition-colors hover:bg-paper"
                            >
                              {template.mockupType}
                            </button>
                          )}
                        </div>
                      ))}
                      {positionTemplates.length === 0 && (
                        <p className="px-2 py-1 text-[11px] text-mute">
                          No mockup templates yet
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {positions.length === 0 && (
                <p className="text-sm text-ink-2">No positions.</p>
              )}
            </PillGroup>
          </Card>
        </aside>

        {/* Center: visual editor */}
        <section className="min-w-0 flex-1">
          {hasMockup && draft ? (
            <Card padding="sm">
              <PrintAreaEditor
                baseImageUrl={baseImageUrl}
                mockupWidth={mockupWidth}
                mockupHeight={mockupHeight}
                backgroundColor={editorColorLayer(
                  activeTemplate,
                  simulatedColor
                )}
                overlays={overlays}
                cutoutMaskUrl={cutoutMaskUrl}
                maskEnabled={layerVisibility.mask}
                area={draft}
                rotation={draft.rotation}
                showSampleDesign={showSampleDesign}
                lockHeight={!missingField}
                suggestion={frameSource === 'suggestion'}
                layerOrder={normalizeLayerOrder(activeTemplate?.layerOrder)}
                baseOpacity={activeTemplate?.baseOpacity ?? 1}
                onChange={(area) =>
                  setDraft((prev) =>
                    prev ? lockFrame({ ...prev, ...area }) : prev
                  )
                }
              />

              {/* Layer 0 simulation: preview on a product color */}
              {(product.colors?.length ?? 0) > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ink-2">
                  <span className="dc-eyebrow">Simulate color:</span>
                  <button
                    type="button"
                    onClick={() => setSimulatedColorId(null)}
                    title="No color (transparent)"
                    aria-label="No color (transparent)"
                    className={`h-6 w-6 rounded-pill border-2 bg-paper ${
                      simulatedColorId === null
                        ? 'border-primary ring-2 ring-primary-soft'
                        : 'border-line'
                    }`}
                    style={CHECKERBOARD_STYLE}
                  />
                  {product.colors?.map((color) => (
                    <button
                      key={color.id}
                      type="button"
                      onClick={() => setSimulatedColorId(color.id)}
                      title={`${color.name}${color.isHeathered ? ' (heathered)' : ''}`}
                      aria-label={`${color.name}${color.isHeathered ? ' (heathered)' : ''}`}
                      className={`h-6 w-6 rounded-pill border-2 ${
                        simulatedColorId === color.id
                          ? 'border-primary ring-2 ring-primary-soft'
                          : 'border-line'
                      }`}
                      style={{ backgroundColor: color.hexCode }}
                      data-testid={`simulate-color-${color.id}`}
                    />
                  ))}
                  {simulatedColor?.isHeathered && (
                    <span className="text-xs text-mute">heathered color</span>
                  )}
                </div>
              )}

              {/* Preview layer toggles */}
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-ink-2">
                <span className="dc-eyebrow">Preview layers:</span>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={showSampleDesign}
                    onChange={(e) => setShowSampleDesign(e.target.checked)}
                    className="accent-primary"
                  />
                  <span>Sample design</span>
                </label>
                {overlays.map((overlay) => (
                  <label
                    key={overlay.id}
                    className="flex cursor-pointer items-center gap-1.5"
                  >
                    <input
                      type="checkbox"
                      checked={
                        layerVisibility[
                          overlay.id as keyof typeof layerVisibility
                        ]
                      }
                      onChange={(e) =>
                        setLayerVisibility((prev) => ({
                          ...prev,
                          [overlay.id]: e.target.checked,
                        }))
                      }
                      className="accent-primary"
                    />
                    <span className="capitalize">{overlay.id}</span>
                  </label>
                ))}
                {cutoutMaskUrl && (
                  <label className="flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={layerVisibility.mask}
                      onChange={(e) =>
                        setLayerVisibility((prev) => ({
                          ...prev,
                          mask: e.target.checked,
                        }))
                      }
                      className="accent-primary"
                    />
                    <span>Cutout mask</span>
                  </label>
                )}
              </div>

              <p className="mt-2 text-xs text-mute">
                Creator layer stack: color →{' '}
                {normalizeLayerOrder(activeTemplate?.layerOrder)
                  .map(
                    (layer) =>
                      ({
                        base: 'base image (shading)',
                        design: 'design',
                        heathered: 'heathered (heathered colors only)',
                        texture: 'texture',
                      })[layer] ?? layer
                  )
                  .join(' → ')}{' '}
                → shadow → cutout mask → overlay
              </p>
            </Card>
          ) : (
            /* No mockup yet: lock the editor and point to the upload flow */
            <EmptyState
              className="min-h-[420px]"
              icon={<ImageOff className="h-7 w-7" aria-hidden="true" />}
              title="No mockup for this position yet"
              description="Upload a mockup template first — the design area is positioned on top of the mockup image."
              action={
                <Button
                  onClick={() => setSidebarTab('mockups')}
                  data-testid="upload-mockup-cta"
                >
                  Upload a mockup
                </Button>
              }
            />
          )}
        </section>

        {/* Right: settings */}
        <aside className="w-80 flex-shrink-0">
          <Card padding="sm">
            <Tabs
              value={sidebarTab}
              onValueChange={(value) => {
                if (isSidebarTab(value)) setSidebarTab(value);
              }}
            >
              <TabsList className="mb-4">
                <TabsTrigger value="area" className="flex-1">
                  Design Area
                </TabsTrigger>
                <TabsTrigger value="mockups" className="flex-1">
                  Mockup Layers
                </TabsTrigger>
              </TabsList>

              <TabsContent value="area" className="space-y-4">
                {/* Pricing does not depend on a configured design area, so
                    this sits outside the draft guard below — an unpriced
                    position with no mockup yet must still be priceable. */}
                {selectedPosition && product && (
                  <div className="space-y-4 rounded-card p-4 ring-1 ring-line">
                    <PositionSurchargeField
                      productId={product.id}
                      positionId={selectedPosition.id}
                      value={selectedPosition.printSurcharge}
                    />
                    <PositionDecorationMethodsPanel
                      productId={product.id}
                      positionId={selectedPosition.id}
                      positionName={selectedPosition.name}
                      assignments={selectedPosition.decorationMethods}
                    />
                  </div>
                )}

                {draft && (
                  <div className="space-y-4 rounded-card p-4 ring-1 ring-line">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="dc-h3 text-sm text-ink">
                          Rahmen auf dieser Vorlage (px)
                        </h3>
                        {activeTemplate && (
                          <CalibrationStatusPill status={frameStatus} />
                        )}
                      </div>
                      <p className="text-xs text-ink-2">
                        {!hasMockup
                          ? 'Upload a mockup first to configure the design area.'
                          : frameSource === 'draft'
                            ? 'Gespeicherter Entwurf: prüfen und übernehmen oder neu ziehen.'
                            : frameSource === 'suggestion'
                              ? 'Startvorschlag: Rahmen ziehen und speichern, erst dann gilt er.'
                              : 'Breite und Lage ziehen oder eingeben; die Höhe folgt den mm.'}
                      </p>
                      {missingField && (
                        <p
                          role="alert"
                          className="mt-2 rounded-control bg-paper-3 px-3 py-2 text-xs text-ink"
                          data-testid="missing-mm-hint"
                        >
                          Kalibrieren gesperrt: {MM_FIELD_LABEL[missingField]}{' '}
                          fehlt. Erst die mm der Fläche eintragen.
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {(
                        [
                          ['x', 'X'],
                          ['y', 'Y'],
                          ['width', 'Width'],
                          ['height', 'Height'],
                        ] as [keyof EditableArea, string][]
                      ).map(([field, label]) => (
                        <FormField key={field} label={label}>
                          <Input
                            type="number"
                            min={0}
                            value={draft[field]}
                            // The height follows the mm (R8).
                            disabled={
                              !hasMockup ||
                              (field === 'height' && !missingField)
                            }
                            onChange={(e) =>
                              updateDraftField(
                                field,
                                Number(e.target.value) || 0
                              )
                            }
                          />
                        </FormField>
                      ))}
                      <FormField label="Rotation (°)">
                        <Input
                          type="number"
                          min={-360}
                          max={360}
                          value={draft.rotation}
                          disabled={!hasMockup}
                          onChange={(e) =>
                            updateDraftField(
                              'rotation',
                              Number(e.target.value) || 0
                            )
                          }
                        />
                      </FormField>
                    </div>

                    <div className="border-t border-line pt-3">
                      <h3 className="dc-h3 text-sm text-ink">
                        Max print size (mm)
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        <FormField label="Width">
                          <Input
                            type="number"
                            min={0}
                            value={draft.maxWidthMm ?? ''}
                            placeholder="—"
                            disabled={!hasMockup}
                            onChange={(e) =>
                              setDraft((prev) =>
                                prev
                                  ? lockFrame({
                                      ...prev,
                                      maxWidthMm: e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                    })
                                  : prev
                              )
                            }
                          />
                        </FormField>
                        <FormField label="Height">
                          <Input
                            type="number"
                            min={0}
                            value={draft.maxHeightMm ?? ''}
                            placeholder="—"
                            disabled={!hasMockup}
                            onChange={(e) =>
                              setDraft((prev) =>
                                prev
                                  ? lockFrame({
                                      ...prev,
                                      maxHeightMm: e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                    })
                                  : prev
                              )
                            }
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="border-t border-line pt-3">
                      <h3 className="dc-h3 text-sm text-ink">Size tier</h3>
                      <p className="mt-1 text-xs text-mute">
                        Derived from the max width by the catalogue sync:{' '}
                        <span className="font-semibold text-ink">
                          {
                            SIZE_TIER_LABELS[
                              selectedPosition?.printArea?.sizeTier ??
                                deriveSizeTier(draft.maxWidthMm)
                            ]
                          }
                        </span>
                        . Onto a Klein area the carry-over maximises the design;
                        Standard keeps the relative placement.
                      </p>
                      <label className="mt-2 flex items-center gap-2 text-sm text-ink">
                        <Checkbox
                          aria-label="Rundum"
                          checked={draft.sizeTierOverride === 'RUNDUM'}
                          disabled={!hasMockup}
                          onChange={(e) =>
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    sizeTierOverride: e.target.checked
                                      ? 'RUNDUM'
                                      : null,
                                  }
                                : prev
                            )
                          }
                        />
                        Rundum (strip around a mug)
                      </label>
                      {draft.sizeTierOverride === 'RUNDUM' && (
                        <p className="mt-1 text-xs text-mute">
                          A design from another product is maximised inside the
                          front half, centred at 25 %. A percent box does not
                          apply here.
                        </p>
                      )}
                      {draft.sizeTierOverride === 'KLEIN' && (
                        <p role="alert" className="mt-1 text-xs text-warning">
                          Migration retire_klein_override pending: this area
                          still carries the retired KLEIN override. The rule
                          reads it as percent 100, centred; saving clears it.
                        </p>
                      )}
                    </div>

                    <AreaCarryOverFields
                      values={draft}
                      product={product}
                      isRundum={draft.sizeTierOverride === 'RUNDUM'}
                      disabled={!hasMockup}
                      onChange={(patch) =>
                        setDraft((prev) =>
                          prev ? { ...prev, ...patch } : prev
                        )
                      }
                    />

                    {mmChanged && (
                      <p className="text-xs text-mute">
                        Geänderte mm setzen die kalibrierten Vorlagen dieser
                        Position auf „prüfen“ ({templatesToReview} weitere).
                      </p>
                    )}
                    <Button
                      className="w-full"
                      disabled={!canSave || isSaving}
                      loading={isSaving}
                      loadingLabel="Speichert…"
                      onClick={() => void handleSave()}
                    >
                      Speichern
                    </Button>
                    {frameSource === 'draft' && (
                      <Button
                        variant="outline"
                        className="w-full"
                        disabled={
                          isSaving || frameDirty || areaDirty || !!missingField
                        }
                        onClick={() => void handleSave({ confirmDraft: true })}
                        title="Den gespeicherten Entwurf unverändert als Kalibrierung übernehmen"
                      >
                        Entwurf übernehmen
                      </Button>
                    )}
                    {saveError && (
                      <p
                        role="alert"
                        className="text-xs text-danger"
                        data-testid="save-error"
                      >
                        {saveError}
                      </p>
                    )}
                    {savedHint && !saveError && (
                      <p
                        role="status"
                        className="text-xs text-mute"
                        data-testid="save-hint"
                      >
                        {savedHint}
                      </p>
                    )}
                    {notSelectable && (
                      <NotSelectableBanner warning={notSelectable.warning} />
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="mockups">
                {selectedPosition && (
                  <MockupLayersPanel
                    productId={product.id}
                    positionSlug={selectedPosition.slug}
                    templates={positionTemplates}
                    activeTemplateId={activeTemplate?.id ?? null}
                  />
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </aside>
      </div>

      {/* Price overrides are product-scoped, so they sit below the
          position columns — inside a position tab they would read as
          per-position pricing. */}
      <ProductCarryOverPanel product={product} />

      <ProductMethodPriceOverrides
        productId={product.id}
        positions={positions}
        overrides={product.decorationMethodPrices}
      />
    </div>
  );
}
